import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useOrgNavigation } from '../../utils/navigation';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const { isOrgPath, getOrgIdFromPath, navigateToOrgPath } = useOrgNavigation();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Allow access to organization selection page without orgId
  if (location.pathname === '/organization-selection') {
    return <>{children}</>;
  }

  // If user is authenticated but doesn't have orgId and not on organization selection page, redirect to organization selection
  if (!user?.orgId) {
    return <Navigate to="/organization-selection" replace />;
  }

  // Handle organization-based routing
  if (!isOrgPath(location.pathname)) {
    // If current path doesn't include organization ID, redirect to organization-aware path
    const basePath = location.pathname === '/' ? '/map' : location.pathname;
    const orgPath = navigateToOrgPath(basePath);
    return <Navigate to={orgPath} replace />;
  }

  // Check if the organization ID in the URL matches the user's organization
  const urlOrgId = getOrgIdFromPath(location.pathname);
  if (urlOrgId && urlOrgId !== user.orgId) {
    // Organization ID mismatch, redirect to user's organization
    const basePath = location.pathname.split('/').slice(3).join('/');
    const orgPath = navigateToOrgPath('/' + basePath);
    return <Navigate to={orgPath} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
